import React, { useRef, useState, useEffect, useReducer, createContext, useContext } from 'react';
import styled, { css } from 'styled-components';
import { fontStyle, themeLight, spacing, borderRadius } from '@naf/theme';
import { nafColor } from '@nafcore/theme';
import { Search as SearchIcon } from '@styled-icons/material/Search';
import { Cancel } from '@styled-icons/material/Cancel';
import { Button } from '@naf/button';
import { Loader } from '@naf/loader';
import * as qs from 'qs';
import { propValidationConfig } from './propValidationConfig';
const getURLQuery = () => {
  if (typeof window !== 'undefined') {
    const parsedLocation = new URL(window.location.href);
    let params = {};
    if (parsedLocation && !!parsedLocation.search) {
      params = qs.parse(parsedLocation.search, {
        ignoreQueryPrefix: true
      });
    }
    return params;
  }
  return {};
};

// TODO: Fix TS

const reducer = (state, action) => {
  switch (action.type) {
    case 'SET_WIDTH':
      return {
        ...state,
        size: action.payload
      };
    case 'SET_QUERY':
      return {
        ...state,
        query: action.payload
      };
    case 'SET_FOCUS':
      return {
        ...state,
        focus: action.payload
      };
    default:
      return state;
  }
};
export const SearchContext = /*#__PURE__*/createContext({
  size: '',
  query: '',
  focus: false
});
export const Search = ({
  withButton = true,
  maxWidth,
  children,
  handleSubmit,
  handleChange,
  placeholder = 'Søk',
  buttonText = 'Søk',
  handleClear,
  className
}) => {
  const inputWrapRef = useRef(null);
  const formRef = useRef(null);
  const inputRef = useRef();
  const [mouseDown, setMouseDown] = useState(false);
  const removeOutline = () => {
    setMouseDown(true);
  };
  const addOutline = e => {
    if (e.key === 'Tab') {
      setMouseDown(false);
    }
  };
  const [state, dispatch] = useReducer(reducer, {
    size: 0
  });
  const value = {
    state,
    dispatch
  };
  useEffect(() => {
    handleResize();
  }, []);
  useEffect(() => {
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [inputWrapRef]);
  useEffect(() => {
    window.addEventListener('mousedown', e => {
      handleClickOutside(e);
      removeOutline();
    });
    return () => {
      window.removeEventListener('mousedown', e => {
        handleClickOutside(e);
        removeOutline();
      });
    };
  }, []);
  useEffect(() => {
    window.addEventListener('keydown', addOutline);
    return () => window.removeEventListener('keydown', addOutline);
  }, []);
  useEffect(() => {
    const parsedParams = getURLQuery();
    if (typeof parsedParams.query !== 'undefined') {
      dispatch({
        type: 'SET_QUERY',
        payload: parsedParams.query
      });
    }
  }, []);

  //Handle resize to set width of suggestions
  const handleResize = () => {
    const inputSize = inputWrapRef.current.clientWidth;
    dispatch({
      type: 'SET_WIDTH',
      payload: inputSize
    });
  };

  //Handle click outside to hide suggestions
  const handleClickOutside = e => {
    setTimeout(() => {
      if (!!formRef.current && !formRef.current.contains(e.target)) {
        dispatch({
          type: 'SET_FOCUS',
          payload: false
        });
      }
    });
  };
  const onSubmit = e => {
    e.preventDefault();
    handleSubmit(state.query);
    inputRef.current.blur();
    dispatch({
      type: 'SET_FOCUS',
      payload: false
    });
  };
  const onChange = query => {
    dispatch({
      type: 'SET_QUERY',
      payload: query
    });
    handleChange(query);
  };
  const clear = () => {
    if (handleClear) {
      handleClear();
    }
    dispatch({
      type: 'SET_FOCUS',
      payload: false
    });
    dispatch({
      type: 'SET_QUERY',
      payload: ''
    });
  };
  return /*#__PURE__*/React.createElement(SearchContext.Provider, {
    value: value
  }, /*#__PURE__*/React.createElement(Form, {
    ref: formRef,
    onSubmit: e => onSubmit(e),
    maxWidth: maxWidth,
    className: className,
    id: mouseDown ? 'mousedown' : ''
  }, /*#__PURE__*/React.createElement(InputWrap, {
    hasFocus: state.focus,
    ref: inputWrapRef
  }, /*#__PURE__*/React.createElement(SearchIcon, {
    size: 24
  }), /*#__PURE__*/React.createElement(Input, {
    ref: inputRef,
    placeholder: placeholder,
    onFocus: () => dispatch({
      type: 'SET_FOCUS',
      payload: true
    }),
    value: state.query || '',
    onChange: e => onChange(e.target.value),
    "aria-label": "Fyll inn s\xF8ketekst"
  }), /*#__PURE__*/React.createElement(ClearButton, {
    type: "button",
    onClick: () => clear(),
    show: state.query,
    "aria-hidden": state.query && state.query.length <= 0,
    "aria-label": "Fjern s\xF8ketekst"
  }, /*#__PURE__*/React.createElement(Cancel, {
    size: 24
  }))), withButton ? /*#__PURE__*/React.createElement(StyledButton, {
    type: "button",
    onClick: () => dispatch({
      type: 'SET_FOCUS',
      payload: false
    })
  }, buttonText) : null, state.query && state.focus && children && /*#__PURE__*/React.createElement(React.Fragment, null, children)));
};

// Search List

export const SearchList = ({
  children,
  loading = false,
  empty = false
}) => {
  const {
    state
  } = useContext(SearchContext);
  return /*#__PURE__*/React.createElement(Suggestions, {
    size: state.size
  }, /*#__PURE__*/React.createElement(List, {
    loading: loading,
    empty: empty
  }, /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(React.Fragment, null, loading && /*#__PURE__*/React.createElement(Loader, null)), /*#__PURE__*/React.createElement(React.Fragment, null, !loading && !empty ? children : null), /*#__PURE__*/React.createElement(React.Fragment, null, !loading && empty ? /*#__PURE__*/React.createElement("span", null, "Finner ingen resultater for ", /*#__PURE__*/React.createElement("u", null, state.query), ".") : null))));
};

// Search Item

export const SearchItem = ({
  /**
   * Tekst på søkeforslag.
   */
  label,
  /**
   * Verdi eller innhold for søkeforslag.
   */
  value,
  /**
   * Funksjon når forslag velges.
   */
  handleClick
}) => {
  const {
    dispatch
  } = useContext(SearchContext);
  const onClick = () => {
    handleClick({
      label,
      value
    });
    dispatch({
      type: 'SET_QUERY',
      payload: label
    });
    dispatch({
      type: 'SET_FOCUS',
      payload: false
    });
  };
  return /*#__PURE__*/React.createElement(Item, null, /*#__PURE__*/React.createElement(SuggestionButton, {
    onClick: () => onClick(),
    type: "button"
  }, label));
};
export default Search;

//Styling
const Form = styled.form.withConfig(propValidationConfig).withConfig({
  displayName: "search__Form",
  componentId: "sc-t78s09-0"
})(["display:flex;position:relative;", " &#mousedown *:focus{outline:0;}"], ({
  maxWidth
}) => maxWidth && css(["max-width:", "px;"], maxWidth));
const List = styled.ul.withConfig(propValidationConfig).withConfig({
  displayName: "search__List",
  componentId: "sc-t78s09-1"
})(["padding:0;list-style:none;margin:0;background-color:", ";display:flex;flex-direction:column;", " ", ""], ({
  theme
}) => theme.background ? theme.background.default : themeLight.background.default, ({
  loading
}) => loading && css(["justify-content:center;align-items:center;padding:", ";height:", ";"], spacing.space16, spacing.space80), ({
  empty
}) => empty && css(["display:flex;justify-content:center;align-items:center;padding:", ";height:", ";"], spacing.space16, spacing.space80));
const Item = styled.li.withConfig({
  displayName: "search__Item",
  componentId: "sc-t78s09-2"
})(["height:", ";display:flex;align-items:center;"], spacing.space48);
const SuggestionButton = styled.button.withConfig({
  displayName: "search__SuggestionButton",
  componentId: "sc-t78s09-3"
})(["border:0;appearance:none;width:100%;height:100%;padding:0 ", ";background-color:", ";text-align:start;", ";&:hover,&:focus{background:", ";}&:hover{cursor:pointer;}"], spacing.space12, ({
  theme
}) => theme.background ? theme.background.default : themeLight.background.default, fontStyle.bodyText.bodyText, nafColor.signature.yellow10);
const InputWrap = styled.div.withConfig(propValidationConfig).withConfig({
  displayName: "search__InputWrap",
  componentId: "sc-t78s09-4"
})(["display:flex;align-items:center;width:100%;border:1px solid;border-color:", ";border-radius:", ";padding:0 ", ";box-sizing:border-box;height:", ";background-color:", ";&:focus-within{border:2px solid;border-color:", ";padding:0 calc(", " - 1px);}svg{color:", ";width:", ";}"], ({
  theme
}) => theme.border ? theme.border.default : themeLight.border.default, borderRadius.M, spacing.space16, spacing.space48, ({
  theme
}) => theme.background ? theme.background.default : themeLight.background.default, ({
  theme
}) => theme.componentColors ? theme.componentColors.inputElement.borderActive : themeLight.componentColors.inputElement.borderActive, spacing.space16, ({
  theme
}) => theme.typography ? theme.typography.subtleText : themeLight.typography.subtleText, spacing.space24);
const ClearButton = styled.button.withConfig({
  displayName: "search__ClearButton",
  componentId: "sc-t78s09-5"
})(["display:flex;justify-content:center;align-items:center;padding:0;border:0;appearance:none;background:transparent;visibility:hidden;width:", ";svg{width:100%;}&:hover{cursor:pointer;}", ""], spacing.space20, ({
  show
}) => show && css(["visibility:visible;"]));
const StyledButton = styled(Button).withConfig({
  displayName: "search__StyledButton",
  componentId: "sc-t78s09-6"
})(["margin-left:", ";&:hover{cursor:pointer;}"], spacing.space8);
const Input = styled.input.withConfig({
  displayName: "search__Input",
  componentId: "sc-t78s09-7"
})(["border:0;border-radius:", ";background:none;appearance:none;height:", ";padding:", ";box-sizing:border-box;width:100%;outline:0;", ""], borderRadius.M, spacing.space48, spacing.space8, fontStyle.article.articleText);
const Suggestions = styled.div.withConfig(propValidationConfig).withConfig({
  displayName: "search__Suggestions",
  componentId: "sc-t78s09-8"
})(["box-shadow:0 2px 12px rgba(0,0,0,0.24);", " position:absolute;top:", ";box-sizing:border-box;border-radius:", ";max-height:192px;overflow-y:scroll;z-index:9;width:", "px;"], fontStyle.article.articleText, spacing.space56, borderRadius.M, ({
  size
}) => size);
//# sourceMappingURL=search.js.map